import { failedAfterApplying } from '@qim/shared'
import { pathFor } from '../routing.js'
import type { DeploymentDetail, RunSummary } from '../api.js'
import type { JSX } from 'react'

/**
 * What to do about an instance the installer could not confirm (IM-185).
 *
 * The installer exits 10 or 11 after Terraform applied when it could not verify
 * the result (MP-3615), and its contract names the remedy for each. A status
 * word alone says something is wrong and not what to do, and both remedies are
 * one action in this console — so the action is named where the status is.
 *
 * Three cases, from the runs the screen already has:
 *
 * - **The instance is `unverified` after an exit 10.** Lost access to the
 *   cluster, so the post-deploy steps were skipped. Re-run apply.
 * - **`unverified` after an exit 11.** Reachable, and a check failed. The log
 *   ends with the installer's reasons; fix the cause and re-run apply.
 * - **The newest run is an `output` that exited 10.** The instance is whatever
 *   it was — an output changes nothing — but the endpoints that run read may
 *   have holes. Refresh endpoints again.
 *
 * Nothing renders otherwise, which is the commonest case by far.
 */
export const UnverifiedNotice = ({
  deployment,
  tenantId,
  onOpen,
}: {
  readonly deployment: Pick<DeploymentDetail, 'status' | 'history'>
  readonly tenantId: string
  readonly onOpen: (path: string) => void
}): JSX.Element | null => {
  const cause = causeOf(deployment)
  if (cause === undefined) return null

  const runPath = pathFor({ screen: 'run', tenantId, runId: cause.run.runId })
  const unreachable = cause.run.failure?.reason === 'applied-unreachable'

  return (
    <div className="verify-note" role="status">
      <p>
        {cause.run.command === 'output'
          ? 'The last output run could not reach the cluster, so the endpoints it read may be incomplete. Refresh endpoints to read them again.'
          : unreachable
            ? 'Terraform applied, but the installer lost access to the cluster before it could check the deployment, and skipped the steps after it. Re-run apply with working credentials.'
            : "Terraform applied, but the installer's checks failed. Its reasons are at the end of the run log. Fix the cause and re-run apply."}
      </p>
      <p>
        <a
          href={runPath}
          onClick={(event) => {
            event.preventDefault()
            onOpen(runPath)
          }}
        >
          Open the {cause.run.command} run
        </a>
      </p>
    </div>
  )
}

/** The run behind the notice, or nothing to say. History is newest first. */
const causeOf = (
  deployment: Pick<DeploymentDetail, 'status' | 'history'>,
): { readonly run: RunSummary } | undefined => {
  if (deployment.status === 'unverified') {
    const run = deployment.history.find(
      (candidate) => candidate.command === 'apply' && failedAfterApplying(candidate),
    )
    return run === undefined ? undefined : { run }
  }
  const newest = deployment.history[0]
  if (
    newest?.command === 'output' &&
    newest.state === 'failed' &&
    newest.failure?.reason === 'applied-unreachable'
  ) {
    return { run: newest }
  }
  return undefined
}
